/**
 * 260813nrai: person name hover card (clients / employees).
 * Prefetch after 300ms, show after 800ms; one floating panel, AJAX-cached HTML.
 */

import { generateAvatarCircleFill, applyAvatarPhoto } from './redfish';

const PRELOAD_DELAY_MS = 300;
const SHOW_DELAY_MS = 800;
const CLOSE_DELAY_MS = 180;

let panel = null;
let preloadTimer = null;
let showTimer = null;
let closeTimer = null;
let activeTrigger = null;
let hoverToken = 0;
let openedByClick = false;
const cache = new Map();
const inflight = new Map();

function prefersFineHover() {
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function hoverCardUrl() {
  return document.body?.dataset?.personHoverCardUrl || '/people/ajax_hover_card.html';
}

function cancelTimers() {
  if (preloadTimer) {
    clearTimeout(preloadTimer);
    preloadTimer = null;
  }
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (closeTimer) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
}

function ensurePanel() {
  if (panel) return panel;
  panel = document.createElement('div');
  panel.className = 'person-hover-card';
  panel.setAttribute('role', 'tooltip');
  panel.hidden = true;
  document.body.appendChild(panel);
  panel.addEventListener('mouseenter', () => {
    cancelTimers();
  });
  panel.addEventListener('mouseleave', (event) => {
    if (openedByClick) return;
    const related = event.relatedTarget;
    if (related && personFromElement(related)) return;
    scheduleClose();
  });
  return panel;
}

function inMenubarChrome(node) {
  return !!(node && node.closest && node.closest('#header, .top-bar, .top-bar-section'));
}

function personFromElement(node) {
  if (!node || !node.closest) return null;
  if (node.closest('.person-hover-card')) return null;
  // 260820nrai: never on menubar / header (covers Stammdaten, admin dropdowns, avatar)
  if (inMenubarChrome(node)) return null;

  // 260820nrai: opt-in only — a[href*="/employees/"][href*="/edit"] also matched
  // tab/stammdaten/chrome links and popped the card over the admin menu
  const trigger = node.closest('.person-hover-link');
  if (!trigger) return null;
  if (trigger.closest('.person-hover-card')) return null;
  if (trigger.getAttribute('data-person-hover') === 'off') return null;
  if (trigger.classList.contains('button')) return null;

  let type = trigger.dataset.personType;
  let id = trigger.dataset.personId;
  if (!type || !id) {
    const href = trigger.getAttribute('href') || '';
    if (href.includes('#')) return null;
    const match = href.match(/\/(clients|employees)\/(\d+)\/edit(?:$|[/?])/);
    if (!match) return null;
    type = match[1] === 'employees' ? 'employee' : 'client';
    id = match[2];
  }
  return { trigger, type, id: String(id) };
}

function loadCard(type, id) {
  const key = `${type}-${id}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  const url = `${hoverCardUrl()}?id=${encodeURIComponent(id)}&type=${encodeURIComponent(type)}`;
  const request = fetch(url, {
    headers: {
      'X-Requested-With': 'XMLHttpRequest'
    },
    credentials: 'same-origin'
  }).then((response) => {
    if (!response.ok) throw new Error(String(response.status));
    return response.text();
  }).then((html) => {
    cache.set(key, html);
    inflight.delete(key);
    return html;
  }).catch((error) => {
    inflight.delete(key);
    throw error;
  });

  inflight.set(key, request);
  return request;
}

function positionPanel(trigger) {
  if (!panel || !trigger) return;
  const rect = trigger.getBoundingClientRect();
  const margin = 8;
  const gap = 6;
  const panelWidth = panel.offsetWidth;
  const panelHeight = panel.offsetHeight;
  let top = rect.bottom + gap;
  let left = rect.left;

  if (top + panelHeight > window.innerHeight - margin) {
    top = rect.top - panelHeight - gap;
    panel.style.transformOrigin = 'bottom center';
  } else {
    panel.style.transformOrigin = 'top center';
  }
  if (left + panelWidth > window.innerWidth - margin) {
    left = window.innerWidth - panelWidth - margin;
  }
  if (left < margin) left = margin;
  if (top < margin) top = margin;

  panel.style.top = `${Math.round(top)}px`;
  panel.style.left = `${Math.round(left)}px`;
}

function hidePanel() {
  if (!panel) return;
  panel.classList.remove('is-open');
  panel.hidden = true;
  panel.innerHTML = '';
  activeTrigger = null;
  openedByClick = false;
}

function fillHoverCardAvatar(card) {
  const avatar = card.querySelector('.js-person-hover-avatar');
  if (!avatar) return;
  const image_url = avatar.getAttribute('data-avatar-url');
  if (image_url) {
    applyAvatarPhoto(avatar, image_url);
    return;
  }
  generateAvatarCircleFill(
    avatar.getAttribute('data-nachname') || '',
    avatar.getAttribute('data-vorname') || '',
    avatar,
    avatar.getAttribute('data-avatar-style') || undefined,
    avatar.getAttribute('data-avatar-kind') || undefined
  );
}

function showPanel(html, trigger) {
  const card = ensurePanel();
  card.innerHTML = html;
  fillHoverCardAvatar(card);
  card.hidden = false;
  card.classList.remove('is-open');
  positionPanel(trigger);
  requestAnimationFrame(() => {
    card.classList.add('is-open');
  });
  activeTrigger = trigger;
}

function revealWhenReady(info, token) {
  loadCard(info.type, info.id).then((html) => {
    if (token !== hoverToken) return;
    showPanel(html, info.trigger);
  }).catch(() => {
    if (token !== hoverToken) return;
    hidePanel();
  });
}

function scheduleOpen(info) {
  cancelTimers();
  if (activeTrigger === info.trigger && panel && !panel.hidden && panel.classList.contains('is-open')) {
    return;
  }

  const token = ++hoverToken;
  const cacheKey = `${info.type}-${info.id}`;

  preloadTimer = setTimeout(() => {
    preloadTimer = null;
    if (token !== hoverToken) return;
    if (!cache.has(cacheKey)) {
      loadCard(info.type, info.id).catch(() => {});
    }
  }, PRELOAD_DELAY_MS);

  showTimer = setTimeout(() => {
    showTimer = null;
    if (token !== hoverToken) return;
    revealWhenReady(info, token);
  }, SHOW_DELAY_MS);
}

function scheduleClose() {
  if (closeTimer) clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    closeTimer = null;
    hoverToken += 1;
    hidePanel();
  }, CLOSE_DELAY_MS);
}

function onDocumentOver(event) {
  if (!prefersFineHover()) return;
  if (event.target.closest && event.target.closest('.person-hover-card')) {
    cancelTimers();
    return;
  }
  if (inMenubarChrome(event.target)) {
    if (panel && !panel.hidden) scheduleClose();
    return;
  }
  const info = personFromElement(event.target);
  if (!info) return;
  cancelTimers();
  scheduleOpen(info);
}

function onDocumentOut(event) {
  if (openedByClick) return;
  if (!prefersFineHover()) return;
  const related = event.relatedTarget;
  if (related && related.closest) {
    if (related.closest('.person-hover-card')) return;
    if (personFromElement(related)) return;
  }
  const leaving = personFromElement(event.target);
  const leavingCard = event.target && event.target.closest && event.target.closest('.person-hover-card');
  if (openedByClick) {
    if (!leavingCard) return;
  } else if (!leaving && !leavingCard) {
    return;
  }
  cancelTimers();
  scheduleClose();
}

function isPersonMenuTrigger(node) {
  return !!(node && node.closest && (
    node.closest('#edit_employee_button') ||
    node.closest('#edit_client_button') ||
    node.closest('.employee-hover-menu-button') ||
    node.closest('.client-hover-menu-button')
  ));
}

function onDocumentClick(event) {
  if (!panel || panel.hidden) return;
  if (event.target.closest && event.target.closest('.person-hover-card')) return;
  if (isPersonMenuTrigger(event.target)) return;
  hoverToken += 1;
  hidePanel();
}

function numericPersonId(raw, compoundKey) {
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw)) raw = raw[0];
  if (raw == null || raw === '') return null;
  const str = String(raw);
  if (compoundKey && typeof window.compoundIdPart === 'function') {
    const part = window.compoundIdPart(str, compoundKey);
    const parsed = parseInt(part, 10);
    if (parsed > 0) return String(parsed);
  }
  if (compoundKey) {
    const match = str.match(new RegExp(`(?:^|_)${compoundKey}_(\\d+)(?:$|_)`));
    if (match) {
      const parsed = parseInt(match[1], 10);
      if (parsed > 0) return String(parsed);
    }
  }
  if (/^\d+$/.test(str)) {
    const parsed = parseInt(str, 10);
    return parsed > 0 ? str : null;
  }
  return null;
}

function menuButtonTrigger(button) {
  if (!button || !button.closest) return button;
  return button.closest('.employee-hover-menu-button, .client-hover-menu-button') || button;
}

/**
 * Bind a select/hidden field hamburger so it opens the person hover card.
 * Looks up the button at click/sync time so AJAX-injected journal forms work
 * even when the inline script runs before the icon HTML.
 *
 * @param {object} options
 * @param {string} options.fieldSelector
 * @param {string} options.buttonSelector
 * @param {string} options.personType `employee` or `client`
 * @param {string} [options.compoundKey] `e` or `c` for compound select values
 */
export function bindSelectPersonHoverMenu(options) {
  const fieldSelector = options && options.fieldSelector;
  const buttonSelector = options && options.buttonSelector;
  const personType = options && options.personType;
  const compoundKey = options && options.compoundKey;
  if (!fieldSelector || !buttonSelector || !personType) return;

  // icon() puts id on <i> and the hover class on the wrapping <span>
  const wrapperSelector = personType === 'client'
    ? '.client-hover-menu-button'
    : '.employee-hover-menu-button';

  function selectedRaw() {
    if (personType === 'client' && typeof window.clientSelectFirstValue === 'function') {
      return window.clientSelectFirstValue();
    }
    if (typeof window.jQuery === 'undefined') return null;
    return window.jQuery(fieldSelector).val();
  }

  function selectedId() {
    return numericPersonId(selectedRaw(), compoundKey);
  }

  function syncButton() {
    if (typeof window.jQuery === 'undefined') return;
    const $trigger = window.jQuery(wrapperSelector);
    if (!$trigger.length) return;
    $trigger.toggleClass('is-visible', !!selectedId());
  }

  if (typeof window.jQuery === 'undefined') return;
  const $doc = window.jQuery(document);
  const ns = `personHoverMenu_${personType}_${String(buttonSelector).replace(/[^a-z0-9]/gi, '')}`;
  // 260820nrai: bind the span, not #id on the inner <i> — .show() made the span
  // fill the journal column so clicks missed the icon and the card never opened
  $doc.off(`click.${ns}`, wrapperSelector).on(`click.${ns}`, wrapperSelector, function (event) {
    event.preventDefault();
    event.stopPropagation();
    const personId = selectedId();
    if (!personId) return;
    openPersonHoverMenu(personType, personId, menuButtonTrigger(this));
  });
  $doc.off(`change.${ns}`, fieldSelector).on(`change.${ns}`, fieldSelector, syncButton);
  syncButton();
  return syncButton;
}

/**
 * Open the hover card immediately (hamburger next to employee/client select).
 */
export function openPersonHoverMenu(type, id, trigger) {
  if (!type || !id || !trigger) return;
  cancelTimers();
  if (activeTrigger === trigger && panel && !panel.hidden) {
    hoverToken += 1;
    hidePanel();
    return;
  }
  const token = ++hoverToken;
  openedByClick = true;
  loadCard(type, String(id)).then((html) => {
    if (token !== hoverToken) return;
    showPanel(html, trigger);
  }).catch(() => {
    if (token !== hoverToken) return;
    hidePanel();
  });
}

/**
 * Bind document-level hover listeners once.
 */
export function initPersonHoverMenu() {
  if (document.documentElement.dataset.personHoverMenuReady === 'true') return;
  document.documentElement.dataset.personHoverMenuReady = 'true';
  document.addEventListener('mouseover', onDocumentOver);
  document.addEventListener('mouseout', onDocumentOut);
  document.addEventListener('click', onDocumentClick);
  // 260820nrai: window scroll only — capturing document scroll also fired for
  // stickyTableHeaders / overflow and closed the client-list flyout immediately
  window.addEventListener('scroll', () => {
    if (panel && !panel.hidden) {
      hoverToken += 1;
      hidePanel();
    }
  }, { passive: true });
  window.addEventListener('resize', () => {
    if (panel && !panel.hidden) {
      hoverToken += 1;
      hidePanel();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && panel && !panel.hidden) {
      hoverToken += 1;
      hidePanel();
    }
  });
}
